import { StyleSheet, Text, View, type TextStyle } from "react-native";

import { typography } from "@heraion/shared";

import { Icon, type IconName } from "../Icon/Icon";
import { scaled } from "../pictureScale";
import { wrappingHeadStyles } from "../primitives/wrappingHeadStyles";
import type { UiTheme } from "../theme";
import type { useChartReadout } from "./pointSelection";

// #388: the head of a chart, one line. The title on the left, never cut; on
// the right the readout of the selected point (useChartReadout), with an
// optional trend glyph and an optional period. When they do not fit one
// line, the right cluster wraps under the title. The chart's current value
// lives here and only here: the legend under the plot does not repeat it.

export type ChartTrend = {
  icon: IconName;
  /** Paints the glyph and the readout. */
  color: string;
  /** What the glyph says to a screen reader; absent = decorative. */
  label?: string;
};

type ReadoutProps = {
  theme: UiTheme;
  readout: string | undefined;
  readoutProps?: ReturnType<typeof useChartReadout>["readoutProps"];
  trend?: ChartTrend;
  /** md instead of sm: the readout is the card's headline number. */
  prominent?: boolean;
};

/** The readout alone, for a head that is not a ChartHead (a Progress
 *  panel's title row passes it as its `right`). */
export function ChartReadout({ theme, readout, readoutProps, trend, prominent }: ReadoutProps) {
  const styles = makeStyles(theme);
  const text = (
    <Text
      style={[
        styles.readout,
        prominent ? styles.prominent : null,
        { color: trend?.color ?? theme.palette.ink },
      ]}
      {...readoutProps}
    >
      {readout}
    </Text>
  );
  if (!trend) return text;
  return (
    <View style={styles.cluster}>
      {/* The sm token in the theme's pixels: an SVG ignores the system
          text size a picture's text sizes cancel. */}
      <Icon
        name={trend.icon}
        size={scaled(theme, typography.sizes.sm)}
        color={trend.color}
        label={trend.label}
      />
      {text}
    </View>
  );
}

export function ChartHead({
  title,
  period,
  ...readout
}: ReadoutProps & {
  title: string;
  /** The window, e.g. «84 d». Only where nothing else on screen states it. */
  period?: string;
}) {
  const styles = makeStyles(readout.theme);
  return (
    <View style={styles.row}>
      <Text style={styles.title}>{title}</Text>
      {period ? (
        <View style={styles.headCluster}>
          <ChartReadout {...readout} />
          <Text style={chartPeriodStyle(readout.theme)}>{period}</Text>
        </View>
      ) : (
        <ChartReadout {...readout} />
      )}
    </View>
  );
}

/** A chart's title voice. MeasurementEmptyRow names a series too short to
 *  chart with it, so the two read as the same list. */
export const chartTitleStyle = (theme: UiTheme): TextStyle => ({
  color: theme.palette.ink2,
  fontSize: theme.typography.sizes.sm,
  fontWeight: "600",
});

/** The window beside a title, here and in PanelTitleRow. */
export const chartPeriodStyle = (theme: UiTheme): TextStyle => ({
  color: theme.palette.ink3,
  fontSize: theme.typography.sizes.xs,
  fontWeight: "600",
  fontFamily: theme.typography.fonts.mono,
  fontVariant: ["tabular-nums"],
});

function makeStyles(theme: UiTheme) {
  const { spacing: s, typography: ty } = theme;
  const head = wrappingHeadStyles(theme);
  return StyleSheet.create({
    row: head.row,
    // The name keeps its width; the readout is what wraps.
    title: { ...chartTitleStyle(theme), ...head.title },
    cluster: { flexDirection: "row", alignItems: "center", gap: s.s1, flexShrink: 1 },
    // Readout and period (and the pill beside them): never run together.
    headCluster: head.cluster,
    readout: {
      fontSize: ty.sizes.sm,
      fontWeight: "700",
      fontFamily: ty.fonts.mono,
      fontVariant: ["tabular-nums"],
      flexShrink: 1,
    },
    prominent: { fontSize: ty.sizes.md },
  });
}
